<script lang="ts" setup>
import type { RefComponent, SettingMarketingTickerValue } from '@/types'

import { computed, onMounted, ref, watch } from 'vue'
import { useBreakpoints, useElementVisibility } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'

import { storeToRefs } from 'pinia'
import { useUiStore } from '@/stores/ui'
import { useCartStore } from '@/stores/customer/cart'
import { useCustomerAuthStore } from '@/stores/customer/auth'

import { useCustomerAuthSettings } from '@/composables/useCustomerAuthSettings'
import { useCustomerSettings } from '@/composables/useCustomerSettings'

import CustomerWrapper from '@/components/Partials/Customer/CustomerWrapper.vue'
import CustomerRoundButton from '@/components/Global/Customer/Catalog/CustomerRoundButton.vue'

const props = defineProps<{
  showOnlyArrow: boolean
  showSidebarMenu?: () => void
}>()

const ui = useUiStore()
const { favorites, searchVisibility } = storeToRefs(ui)

const customerAuthStore = useCustomerAuthStore()
const { authorized: customerAuthorized } = storeToRefs(customerAuthStore)

const router = useRouter()
const route = useRoute()

function goToAccount() {
  if (customerAuthorized.value) {
    router.push({ name: 'customer-account' })

    return
  }

  ui.showCurtainLogin()
}

const cartStore = useCartStore()
const { cartItemsAmount } = storeToRefs(cartStore)

function handleClick() {
  router.push(
    router.resolve({
      path: String(router.options.history.state.back ?? '/'),
    })
  )
}

const { accountSettings } = useCustomerAuthSettings(true)

const showAbove = ref(false)

watch(
  () => searchVisibility.value,
  () => {
    if (searchVisibility.value) {
      showAbove.value = true

      return
    }

    const timeoutId = setTimeout(() => {
      showAbove.value = false

      clearTimeout(timeoutId)
    }, 500)
  }
)
/**
 * Ticker 1 & 2
 * */
const tickerRef1: RefComponent<typeof HTMLLinkElement> = ref(null)
const ticker1IsVisible = useElementVisibility(tickerRef1)

const tickerRef2: RefComponent<typeof HTMLLinkElement> = ref(null)
const ticker2IsVisible = useElementVisibility(tickerRef2)

const { currentSettings: tickerSettings } =
  useCustomerSettings<SettingMarketingTickerValue>('ticker', 'show')

const ticker1Style = computed(() => {
  const bgColor = tickerSettings.value?.value.color_bg ?? '#000000'
  const textColor = tickerSettings.value?.value.color_text ?? '#ffffff'

  return `background-color: ${bgColor}; color: ${textColor};`
})

const ticker2BgColor = computed(
  () => tickerSettings.value?.value.additional_color_bg ?? '#000000'
)
const ticker2TextColor = computed(
  () => tickerSettings.value?.value.additional_color_text ?? '#ffffff'
)
const ticker2Style = computed(() => {
  return `background-color: ${ticker2BgColor.value}; color: ${ticker2TextColor.value};`
})
/**
 * Ticker 2 visibility
 * */
const ticker2Hide = ref(false)

function hideTicker() {
  ticker2Hide.value = true
  ticker2IsVisible.value = false
}

const tickerVisibilityByPage = computed(() => {
  if (!tickerSettings.value?.value.additional_show) return false

  const urls = (tickerSettings.value?.value.additional_urls ?? '')
    .split(';')
    .map((item) => item.trim())

  return urls.some(
    (url) =>
      (url.length > 2 && route.fullPath.includes(url)) || url === route.fullPath
  )
})

onMounted(() => {
  ui.setTickerVisibility(ticker1IsVisible, 1)

  ui.setTickerVisibility(ticker2IsVisible, 2)
  ui.setTickerVisibility(tickerVisibilityByPage, 3)
})

//  const breakpoints = useBreakpoints({
//   md: 768,
//   xl: 1280,
// })
// const isLargeDesktop = breakpoints.greaterOrEqual('xl')
// const isTabletOrSmaller = breakpoints.smallerOrEqual('md')
</script>

<template>
  <div class="sticky top-0 z-30" :class="showAbove ? 'z-[1056]' : ''">
    <!-- Header -->
    <CustomerWrapper class="bg-white">
      <header
        class="flex items-center justify-between relative w-full h-12 lg:h-[52px]"
      >
        <CustomerRoundButton
          v-if="props.showOnlyArrow"
          btn-classes=""
          @click="handleClick"
        >
          <template #default="{ iconColor }">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M21 12H2M2 12L7 7M2 12L7 17"
                :stroke="iconColor"
                stroke-width="1.2"
                stroke-linecap="square"
              />
            </svg>
          </template>
        </CustomerRoundButton>

        <template v-else>
          <!-- Menu -->
          <CustomerRoundButton
            btn-classes=""
            aria-label="Меню"
            @click="props.showSidebarMenu?.()"
          >
            <template #default="{ iconColor }">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 12H22"
                  :stroke="iconColor"
                  stroke-width="1.2"
                  stroke-linecap="square"
                />
                <path
                  d="M2 7H22"
                  :stroke="iconColor"
                  stroke-width="1.2"
                  stroke-linecap="square"
                />
                <path
                  d="M2 17H22"
                  :stroke="iconColor"
                  stroke-width="1.2"
                  stroke-linecap="square"
                />
              </svg>
            </template>
          </CustomerRoundButton>

          <!-- Search -->
          <CustomerRoundButton
            v-if="!/search/.test(String(route.name ?? ''))"
            class="hidden lg:block ml-8 mr-auto"
            btn-classes=""
            aria-label="Поиск"
            @click="searchVisibility = !searchVisibility"
          >
            <template #default="{ iconColor }">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                  :stroke="iconColor"
                  stroke-width="1.2"
                />
                <path d="M21 21L16 16" :stroke="iconColor" stroke-width="1.2" />
              </svg>
            </template>
          </CustomerRoundButton>
        </template>

        <!-- Logo -->
        <div
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ml-1.5"
        >
          <router-link aria-label="SEEN. На главную" to="/">
            <svg
              class="hidden lg:block"
              width="118"
              height="24"
              viewBox="0 0 118 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M46.6357 0V3.98873H34.4802V9.6H45.8569V13.5887H34.4802V20.0113H47.0081V24H29.7398V0H46.6357ZM19.8421 3.55316L17.0703 6.71871C15.6731 5.59893 14.5013 4.85599 13.5548 4.48991C12.5858 4.10229 11.4478 3.90848 10.1408 3.90848C8.81121 3.90848 7.74079 4.16689 6.92953 4.68372C6.1408 5.20054 5.74644 5.87887 5.74644 6.71871C5.74644 7.2786 5.93799 7.74159 6.32108 8.10767C6.72671 8.47376 7.30136 8.76447 8.04501 8.97981C8.78867 9.19516 9.97176 9.48587 11.5943 9.85195C14.7943 10.6057 17.0027 11.4347 18.2196 12.3392C19.4365 13.2436 20.0787 14.6433 20.1463 16.5384C20.1463 18.7349 19.2787 20.533 17.5435 21.9327C15.8309 23.3109 13.5323 24 10.6478 24C8.55205 24 6.69291 23.6878 5.07039 23.0633C3.49293 22.4172 1.8028 21.319 0 19.7685L2.83942 16.5707C4.48448 17.9489 5.83658 18.8641 6.89573 19.3163C8.04502 19.8116 9.32951 20.0592 10.7492 20.0592C12.1689 20.0592 13.307 19.7793 14.1633 19.2194C15.0196 18.638 15.4478 17.8627 15.4478 16.8937C15.4478 16.3123 15.2562 15.8385 14.8731 15.4724C14.5126 15.1279 14.0281 14.8587 13.4196 14.6649C12.8337 14.4495 11.8647 14.2019 10.5126 13.9219L9.36332 13.6635C6.18587 12.9314 4.01124 12.0808 2.83942 11.1117C1.66759 10.1211 1.08168 8.69987 1.08168 6.84792C1.08168 4.90983 1.93801 3.28399 3.65068 1.97039C5.36334 0.6568 7.56051 2.86102e-06 10.2422 2.86102e-06C12.2253 2.86102e-06 13.9154 0.269182 15.3126 0.807539C16.7548 1.3459 18.2647 2.26111 19.8421 3.55316ZM73.4975 3.98873V0H56.6016V24H73.8699V20.0113H61.3419V13.5887H72.7187V9.6H61.3419V3.98873H73.4975ZM99.0356 0H103.61V24H98.5984L91.8717 13.1831C89.9883 10.1183 88.6429 7.85352 87.8357 6.38873C87.9703 9.20563 88.0375 12.3155 88.0375 15.7183V24H83.4634V0H88.5757L94.9996 10.3437L95.9077 11.8648C97.2979 14.1183 98.3966 15.9549 99.2038 17.3746C99.0917 15.0085 99.0356 12.1239 99.0356 8.72113V0ZM118 19.2H113.203V24H118V19.2Z"
                fill="black"
              />
            </svg>
            <svg
              class="lg:hidden"
              width="99"
              height="20"
              viewBox="0 0 99 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M39.5471 0.0384827V3.34964H29.4564V8.00771H38.9006V11.3189H29.4564V16.6504H39.8562V19.9615H25.5214V0.0384827H39.5471ZM17.305 2.98806L15.004 5.61587C13.8442 4.6863 12.8714 4.06957 12.0857 3.76568C11.2813 3.44391 10.3366 3.28302 9.25163 3.28302C8.14792 3.28302 7.25934 3.49754 6.58589 3.92656C5.93115 4.35559 5.60377 4.9187 5.60377 5.61587C5.60377 6.08065 5.76278 6.46499 6.0808 6.76889C6.41753 7.07278 6.89455 7.31411 7.51188 7.49287C8.12921 7.67163 9.11133 7.91296 10.4582 8.21686C13.1146 8.84253 14.9479 9.53076 15.9581 10.2816C16.9683 11.0324 17.5014 12.1943 17.5575 13.7674C17.5575 15.5908 16.8373 17.0835 15.3969 18.2454C13.9751 19.3895 12.067 19.9615 9.67254 19.9615C7.93279 19.9615 6.38947 19.7023 5.04256 19.1839C3.73308 18.6476 2.33005 17.736 0.833496 16.4489L3.19057 13.7942C4.55618 14.9383 5.6786 15.6981 6.55783 16.0735C7.51188 16.4846 8.57818 16.6902 9.75672 16.6902C10.9353 16.6902 11.88 16.4578 12.5908 15.993C13.3017 15.5104 13.6571 14.8668 13.6571 14.0624C13.6571 13.5797 13.4981 13.1865 13.1801 12.8826C12.8808 12.5965 12.4786 12.3731 11.9735 12.2122C11.4871 12.0334 10.6827 11.8279 9.5603 11.5955L8.60624 11.381C5.96856 10.7732 4.16334 10.067 3.19057 9.26262C2.21781 8.44031 1.73143 7.26048 1.73143 5.72313C1.73143 4.11427 2.4423 2.76461 3.86402 1.67416C5.28575 0.58371 7.10968 0.0384855 9.33581 0.0384855C10.982 0.0384855 12.385 0.261938 13.5449 0.708843C14.7421 1.15575 15.9955 1.91549 17.305 2.98806ZM61.8457 3.34964V0.0384827H47.82V19.9615H62.1549V16.6504H51.7551V11.3189H61.1993V8.00771H51.7551V3.34964H61.8457ZM83.0457 0.0384827H86.8428V19.9615H82.6827L77.0987 10.9821C75.5352 8.43797 74.4184 6.55791 73.7484 5.34194C73.86 7.68033 73.9159 10.2619 73.9159 13.0867V19.9615H70.1188V0.0384827H74.3626L79.6953 8.62504L80.4491 9.88777C81.6032 11.7585 82.5152 13.2831 83.1853 14.4617C83.0922 12.4974 83.0457 10.1029 83.0457 7.27813V0.0384827ZM98.7885 15.977H94.8066V19.9616H98.7885V15.977Z"
                fill="black"
              />
            </svg>
          </router-link>
        </div>

        <template v-if="!props.showOnlyArrow">
          <!-- Account -->
          <CustomerRoundButton
            v-if="accountSettings && !accountSettings.value.hide_icon"
            class="hidden ml-auto lg:block mr-8"
            btn-classes=""
            aria-label="Личный кабинет"
            @click="goToAccount"
          >
            <template #default="{ iconColor }">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                :fill="customerAuthorized ? iconColor : 'none'"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="7"
                  r="4"
                  :stroke="iconColor"
                  stroke-width="1.2"
                />
                <path
                  d="M17.25 14H6.75C5.7835 14 5 14.7835 5 15.75C5 18.6495 7.35051 21 10.25 21H12H13.75C16.6495 21 19 18.6495 19 15.75C19 14.7835 18.2165 14 17.25 14Z"
                  :stroke="iconColor"
                  stroke-width="1.2"
                />
              </svg>
            </template>
          </CustomerRoundButton>

          <!-- Favorites -->
          <!-- <CustomerRoundButton
            class="hidden lg:block mr-8"
            btn-classes=""
            :link="{ name: 'customer-favorites' }"
            aria-label="Избранное"
          >
            <template #default="{ iconColor }">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                :fill="favorites.length ? iconColor : 'none'"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.44444 4C8.71932 4 10.667 5.62121 12 7.11688C13.333 5.62121 15.2807 4 17.5556 4C20.0914 4 22 6.07468 22 8.57143C22 12.6084 15.8889 18 12 21C8.11111 18 2 12.9216 2 8.57143C2 6.07468 3.90864 4 6.44444 4Z"
                  :stroke="iconColor"
                  stroke-width="1.2"
                />
              </svg>
            </template>
          </CustomerRoundButton> -->

          <!-- Cart -->
          <div class="relative">
            <CustomerRoundButton
              btn-classes=""
              :link="{ query: { cart: 'show' } }"
              aria-label="Корзина"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 8H6.85206C5.80699 8 4.93811 8.80461 4.85795 9.84661L4.16565 18.8466C4.07629 20.0082 4.99473 21 6.15975 21H17.8402C19.0053 21 19.9237 20.0082 19.8344 18.8466L19.142 9.84661C19.0619 8.80461 18.193 8 17.1479 8H8ZM8 8V11M8 8V7C8 4.79086 9.79086 3 12 3V3C14.2091 3 16 4.79086 16 7V11"
                  stroke="black"
                  stroke-width="1.2"
                  stroke-linecap="square"
                />
              </svg>
            </CustomerRoundButton>
            <!-- Count -->
            <div
              v-if="cartItemsAmount"
              class="cursor-pointer absolute top-1/2 left-1/2 bg-cgreen text-white w-4 h-4 !leading-[17px] text-center text-c2xs font-bold rounded-full"
              @click="router.push({ query: { cart: 'show' } })"
            >
              {{ cartItemsAmount }}
            </div>
          </div>
        </template>
      </header>
    </CustomerWrapper>
  </div>
</template>
